import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";

const CATEGORIES = [
  "Oil & Gas", "Engineering", "IT & Technology", "Logistics & Transport",
  "Manpower & Staffing", "Procurement & Supply", "Legal & Advisory",
  "Construction & Civil", "HSE & Safety", "Finance & Audit", "Other",
];

const Field = ({ label, required, children }) => (
  <div className="mb-4">
    <label className="block text-base font-medium text-slate-300 mb-1.5">
      {label} {required && <span className="text-yellow-600">*</span>}
    </label>
    {children}
  </div>
);

const SupplierModal = ({ supplier, onClose, onSave, saving }) => {
  const isEdit = !!supplier?.id;
  const [form, setForm] = useState({
    Title: supplier?.Title || "",
    Category: supplier?.Category || "",
    ContactPerson: supplier?.ContactPerson || "",
    Email: supplier?.Email || "",
    Phone: supplier?.Phone || "",
    Status: supplier?.Status || "Active",
    Notes: supplier?.Notes || "",
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-8 w-96 max-w-95vw relative shadow-2xl max-h-screen overflow-y-auto">
        <div className="absolute top-0 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-transparent via-yellow-600 to-transparent" />
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-2xl font-medium text-slate-100 m-0">
            {isEdit ? "Edit Supplier" : "Add Supplier"}
          </h3>
          <button onClick={onClose} className="bg-none border-none text-slate-400 hover:text-slate-200 cursor-pointer text-2xl">×</button>
        </div>

        <Field label="Supplier Name" required>
          <input
            value={form.Title}
            onChange={e => set("Title", e.target.value)}
            placeholder="Full legal name of supplier"
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600"
          />
        </Field>
        <Field label="Category" required>
          <select
            value={form.Category}
            onChange={e => set("Category", e.target.value)}
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600"
          >
            <option value="">Select category...</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Contact Person">
            <input
              value={form.ContactPerson}
              onChange={e => set("ContactPerson", e.target.value)}
              placeholder="Primary contact name"
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600"
            />
          </Field>
          <Field label="Phone">
            <input
              value={form.Phone}
              onChange={e => set("Phone", e.target.value)}
              placeholder="+234..."
              className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600"
            />
          </Field>
        </div>
        <Field label="Email">
          <input
            type="email"
            value={form.Email}
            onChange={e => set("Email", e.target.value)}
            placeholder="contact@supplier.com"
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600"
          />
        </Field>
        <Field label="Status">
          <div className="flex gap-4 pt-1">
            {["Active", "Inactive"].map(v => (
              <label key={v} className="flex items-center gap-1.5 text-slate-300 cursor-pointer text-base">
                <input
                  type="radio"
                  name="status"
                  value={v}
                  checked={form.Status === v}
                  onChange={() => set("Status", v)}
                  className="accent-yellow-600"
                />
                {v}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Notes">
          <textarea
            value={form.Notes}
            onChange={e => set("Notes", e.target.value)}
            rows={3}
            placeholder="Any additional notes about this supplier..."
            className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600 resize-vertical"
          />
        </Field>

        <div className="flex gap-2.5 justify-end mt-2">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-transparent border border-slate-700 rounded-lg text-slate-400 cursor-pointer font-sans hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(form, supplier?.id)}
            disabled={!form.Title || !form.Category || saving}
            className={`px-6 py-2.5 border-none rounded-lg cursor-pointer font-sans font-semibold ${
              form.Title && form.Category
                ? "bg-yellow-600 text-slate-900 hover:bg-yellow-500"
                : "bg-slate-700 text-slate-400 cursor-not-allowed"
            }`}
          >
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Supplier"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const SupplierManagement = () => {
  const navigate = useNavigate();
  const { service, addNotification, isAdmin } = useApp();
  const { theme } = useTheme();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("Active");
  const [filterSource, setFilterSource] = useState(""); // "" = all, "local", "srm"
  const [modal, setModal] = useState(null); // null | { supplier } | { supplier: {} } for new
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Fetch local suppliers
      const localData = await service.getListItems("Suppliers").catch(() => ({ value: [] }));
      const localSuppliers = (localData?.value || [])
        .map(i => ({ id: i.id, ...i.fields, source: "local" }))
        .filter(s => s.Status !== "Inactive");

      // Fetch external SRM suppliers
      const externalSiteUrl = "https://lekoillimited.sharepoint.com/sites/SRMDashboard";
      const srmData = await service.getListItemsFromExternalSite(externalSiteUrl, "Suppliers").catch(() => ({ value: [] }));
      const srmSuppliers = (srmData?.value || [])
        .map(i => ({ id: i.id, ...i.fields, source: "srm" }))
        .filter(s => s.Status !== "Inactive");

      // Merge: local first, then SRM (deduplicate by Title)
      const merged = [...localSuppliers];
      const localTitles = new Set(localSuppliers.map(s => (s.Title || "").toLowerCase()));
      srmSuppliers.forEach(s => {
        if (!localTitles.has((s.Title || "").toLowerCase())) {
          merged.push(s);
        }
      });

      // Sort by title
      merged.sort((a, b) => (a.Title || "").localeCompare(b.Title || ""));
      setSuppliers(merged);
      console.log('[SupplierManagement] Loaded suppliers:', { local: localSuppliers.length, srm: srmSuppliers.length, merged: merged.length });
    } catch (error) {
      console.error('[SupplierManagement] Error loading suppliers:', error);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = suppliers.filter(s => {
    const q = search.toLowerCase();
    const matchSearch = !q || (s.Title || "").toLowerCase().includes(q) || (s.Category || "").toLowerCase().includes(q) || (s.ContactPerson || "").toLowerCase().includes(q);
    const matchCat = !filterCategory || s.Category === filterCategory;
    const matchStatus = !filterStatus || s.Status === filterStatus;
    const matchSource = !filterSource || s.source === filterSource;
    return matchSearch && matchCat && matchStatus && matchSource;
  });

  const handleSave = async (fields, id) => {
    if (!fields.Title || !fields.Category) { addNotification("Supplier name and category are required.", "error"); return; }
    setSaving(true);
    try {
      if (id) {
        await service.updateListItem("Suppliers", id, fields);
        addNotification("Supplier updated.", "success");
      } else {
        await service.createListItem("Suppliers", fields);
        addNotification("Supplier added.", "success");
      }
      setModal(null);
      load();
    } catch (e) {
      addNotification("Error saving supplier: " + e.message, "error");
    } finally { setSaving(false); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete supplier "${s.Title}"? This cannot be undone.`)) return;
    try {
      await service.deleteListItem("Suppliers", s.id);
      addNotification("Supplier deleted.", "success");
      load();
    } catch (e) { addNotification("Error: " + e.message, "error"); }
  };

  const activeCount   = suppliers.filter(s => s.Status !== "Inactive").length;
  const inactiveCount = suppliers.filter(s => s.Status === "Inactive").length;

  const getStatGradientColor = (color) => {
    if (color === "#c9a84c") return "from-yellow-600";
    if (color === "#107c10") return "from-green-700";
    if (color === "#797775") return "from-slate-600";
    if (color === "#0078d4") return "from-blue-600";
    return "from-slate-400";
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-7">
        <h1 className="font-serif text-3xl font-medium text-slate-100 mb-1">Supplier Registry</h1>
        <p className="text-slate-400 m-0">{activeCount} active · {inactiveCount} inactive</p>
        <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent mt-2" />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total Suppliers", value: suppliers.length, color: "#c9a84c" },
          { label: "Active", value: activeCount, color: "#107c10" },
          { label: "Inactive", value: inactiveCount, color: "#797775" },
          { label: "Categories", value: [...new Set(suppliers.map(s => s.Category).filter(Boolean))].length, color: "#0078d4" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-slate-900 border border-slate-700 rounded-lg p-3.5 relative overflow-hidden">
            <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${getStatGradientColor(color)} to-transparent`} />
            <div className="text-sm text-slate-400 mb-1">{label}</div>
            <div className="text-2xl font-serif font-semibold text-slate-100 leading-none">{value}</div>
          </div>
        ))}
      </div>

      {/* Filters + actions */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-5">
        <div className="flex gap-2.5 flex-wrap">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, category, contact..."
            className="flex-1 min-w-48 px-3.5 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600"
          />
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="px-2.5 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-2.5 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select
            value={filterSource}
            onChange={e => setFilterSource(e.target.value)}
            className="px-2.5 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 text-base font-sans outline-none focus:border-yellow-600"
          >
            <option value="">All Sources</option>
            <option value="local">Local Only</option>
            <option value="srm">SRM Only</option>
          </select>
          {isAdmin && (
            <>
              <button
                onClick={load}
                className="px-3.5 py-2.5 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg text-slate-300 cursor-pointer text-base font-sans whitespace-nowrap transition-colors"
                title="Refresh suppliers from both local and SRM sources"
              >
                🔄 Sync
              </button>
              <button
                onClick={() => setModal({ supplier: {} })}
                className="px-4.5 py-2.5 bg-yellow-600 border-none rounded-lg text-slate-900 cursor-pointer text-base font-sans font-semibold whitespace-nowrap hover:bg-yellow-500"
              >
                + Add Supplier
              </button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading suppliers...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-base">
              <thead>
                <tr className="bg-slate-950">
                  {["Supplier Name", "Category", "Contact Person", "Email", "Phone", "Source", "Status", ...(isAdmin ? ["Actions"] : [])].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-base font-medium text-slate-400 uppercase tracking-wider border-b border-slate-700 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 9 : 8} className="text-center p-12 text-slate-400">No suppliers found.</td></tr>
                ) : filtered.map((s, i) => (
                  <tr
                    key={s.id}
                    className="border-b border-slate-700 hover:bg-yellow-600/5 transition-colors"
                  >
                    <td className="px-4 py-3 font-semibold text-slate-100">{s.Title}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded bg-slate-800 text-sm text-slate-400">{s.Category || "--"}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{s.ContactPerson || "--"}</td>
                    <td className="px-4 py-3 text-slate-400 text-base">{s.Email || "--"}</td>
                    <td className="px-4 py-3 text-slate-400 text-base font-mono">{s.Phone || "--"}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                        s.source === "local"
                          ? "bg-blue-700/20 text-blue-400"
                          : "bg-purple-700/20 text-purple-400"
                      }`}>
                        {s.source === "local" ? "📱 Local" : "☁️ SRM"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-base font-medium ${
                        s.Status === "Active"
                          ? "bg-green-700/20 text-green-700"
                          : "bg-slate-600/20 text-slate-500"
                      }`}>
                        {s.Status || "Active"}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => setModal({ supplier: s })}
                            className="px-3 py-1 bg-yellow-600/20 border border-yellow-600/50 rounded text-yellow-600 cursor-pointer text-base font-sans hover:bg-yellow-600/30"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(s)}
                            className="px-3 py-1 bg-transparent border border-slate-700 rounded text-slate-400 cursor-pointer text-base hover:bg-slate-800"
                          >
                            Del
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <SupplierModal
          supplier={modal.supplier}
          onClose={() => setModal(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
};
